import React, { useState, useEffect, useCallback } from 'react';
import {
  FaPlus,
  FaTag,
  FaClock,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaExclamationTriangle,
  FaPercent,
  FaRupeeSign,
  FaShoppingCart,
  FaUtensils,
  FaToggleOn,
  FaToggleOff,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaInfoCircle,
  FaArrowLeft
} from 'react-icons/fa';

import { getData, postData, putData, deleteData } from '../../../Services/Fetchnodeservices';
import AddOffer from './AddofferScreen';

const Campaigns = ({ isDark = false }) => {
  // State
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [showEditCampaign, setShowEditCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [showAddOffer, setShowAddOffer] = useState(false);
  const [restaurantId, setRestaurantId] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    offer_name: '',
    offer_type: 'flat',
    offer_scope: 'product',
    offer: '',
    min_cart_value: '0',
    product_id: '',
    product_name: '',
    offer_start: '',
    offer_end: '',
    status: 1,
    buy_quantity: '1',
    free_quantity: '0',
    item_free: null
  });

  // Get restaurantId from localStorage
  const getRestaurantId = useCallback(() => {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const id = userData?.id || userData?.restaurantId || userData?._id;
        if (id) return id;
      }
      
      const directId = localStorage.getItem('restaurantId');
      if (directId) return directId;
      
      const userId = localStorage.getItem('userId');
      if (userId) return userId;
      
      return null;
    } catch (e) {
      console.error('Error getting restaurantId:', e);
      return null;
    }
  }, []);

  // Get discount display
  const getDiscountDisplay = (campaign) => {
    const offer = parseFloat(campaign.offer) || 0;
    const type = campaign.offer_type || 'flat';
    
    if (type === 'percent') return `${offer}% OFF`;
    if (type === 'flat') return `₹${offer} OFF`;
    if (type === 'bogo') return `Buy ${campaign.buy_quantity || 1} Get ${campaign.free_quantity || 0} Free`;
    if (type === 'combo') return `Combo Offer`;
    return `₹${offer} OFF`;
  };

  // Get offer type label
  const getOfferTypeLabel = (type) => {
    const labels = { 'percent': 'Percentage', 'flat': 'Flat', 'bogo': 'BOGO', 'combo': 'Combo' };
    return labels[type] || 'Flat';
  };

  // Update stats
  const updateStats = (campaignData) => {
    setStats({
      total: campaignData.length,
      active: campaignData.filter(c => parseInt(c.status) === 1).length,
      inactive: campaignData.filter(c => parseInt(c.status) === 0).length,
    });
  };

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    const id = getRestaurantId();
    if (!id) {
      setLoading(false);
      setError('Restaurant ID not found. Please login again.');
      return;
    }

    setRestaurantId(id);

    try {
      setLoading(true);
      setError(null);
      const result = await getData(`menu/list/${id}`);

      if (result && result.success) {
        const campaignData = Array.isArray(result.data) ? result.data : [];

        const processedCampaigns = campaignData.map(campaign => ({
          ...campaign,
          status: parseInt(campaign.status) || 0,
          isActive: parseInt(campaign.status) === 1,
          id: campaign.id || campaign._id,
          offer_name: campaign.offer_name || 'Campaign',
          offer_type: campaign.offer_type || 'flat',
          offer_scope: campaign.offer_scope || 'product',
          offer: parseFloat(campaign.offer) || 0,
          min_cart_value: parseFloat(campaign.min_cart_value) || 0,
          product_id: campaign.product_id || '',
          product_name: campaign.product_name || 'All Products',
          offer_start: campaign.offer_start || '',
          offer_end: campaign.offer_end || '',
          buy_quantity: parseInt(campaign.buy_quantity) || 1,
          free_quantity: parseInt(campaign.free_quantity) || 0,
          item_free: campaign.item_free || null,
          designation: campaign.designation || 'restaurant',
          designation_id: campaign.designation_id || id,
          created_at: campaign.created_at || '',
          updated_at: campaign.updated_at || '',
          hide: parseInt(campaign.hide) || 0,
          discountDisplay: getDiscountDisplay(campaign),
          offerTypeLabel: getOfferTypeLabel(campaign.offer_type),
          offerScopeLabel: campaign.offer_scope === 'cart' ? 'Cart Offer' : 'Product Offer',
        }));

        setCampaigns(processedCampaigns);
        setFilteredCampaigns(processedCampaigns);
        updateStats(processedCampaigns);
      } else {
        setError(result?.message || 'Failed to fetch campaigns');
        setCampaigns([]);
        setFilteredCampaigns([]);
      }
    } catch (err) {
      console.error('Fetch campaigns error:', err);
      setError(err.message || 'Something went wrong');
      setCampaigns([]);
      setFilteredCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [getRestaurantId]);

  // Toggle campaign status
  const toggleStatus = async (id) => {
    try {
      setUpdatingId(id);
      const result = await postData(`menu/offer/status/${id}`, {});

      if (result && result.success === true) {
        await fetchCampaigns();
        alert('Campaign status updated successfully!');
      } else {
        alert(result?.message || 'Failed to update status');
        await fetchCampaigns();
      }
    } catch (err) {
      console.error('Toggle status error:', err);
      alert('Error updating status. Please try again.');
      await fetchCampaigns();
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    const id = getRestaurantId();
    if (id) {
      fetchCampaigns();
    } else {
      setLoading(false);
      setError('Please login to view campaigns');
    }
  }, [getRestaurantId, fetchCampaigns]);

  // Filter campaigns by tab
  useEffect(() => {
    let filtered = [...campaigns];
    if (activeTab === 'active') {
      filtered = filtered.filter(c => parseInt(c.status) === 1);
    } else if (activeTab === 'inactive') {
      filtered = filtered.filter(c => parseInt(c.status) === 0);
    }
    setFilteredCampaigns(filtered);
  }, [campaigns, activeTab]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
  };

  // Handle form submit - Update
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setUpdatingId('edit');
      const payload = {
        ...formData,
        designation_id: restaurantId,
        offer: parseFloat(formData.offer) || 0,
        min_cart_value: parseFloat(formData.min_cart_value) || 0,
        buy_quantity: parseInt(formData.buy_quantity) || 1,
        free_quantity: parseInt(formData.free_quantity) || 0,
        hide: 0
      };

      const result = await putData(`menu/offer/update/${selectedCampaign.id}`, payload);

      if (result.success) {
        await fetchCampaigns();
        setShowEditCampaign(false);
        setSelectedCampaign(null);
        resetForm();
        alert('Campaign updated successfully!');
      } else {
        alert(result.message || 'Failed to update campaign');
      }
    } catch (err) {
      console.error('Update campaign error:', err);
      alert('Error updating campaign');
    } finally {
      setUpdatingId(null);
    }
  };

  // Delete campaign
  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign permanently?')) return;
    try {
      setUpdatingId(id);
      const result = await deleteData(`menu/offer/delete/${id}`);
      if (result.success) {
        await fetchCampaigns();
        alert('Campaign deleted successfully!');
      } else {
        alert(result.message || 'Failed to delete campaign');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting campaign');
    } finally {
      setUpdatingId(null);
    }
  };

  // Edit campaign - load data
  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      offer_name: campaign.offer_name || '',
      offer_type: campaign.offer_type || 'flat',
      offer_scope: campaign.offer_scope || 'product',
      offer: campaign.offer || '',
      min_cart_value: campaign.min_cart_value || '0',
      product_id: campaign.product_id || '',
      product_name: campaign.product_name || '',
      offer_start: campaign.offer_start || '',
      offer_end: campaign.offer_end || '',
      status: parseInt(campaign.status) || 0,
      buy_quantity: campaign.buy_quantity || '1',
      free_quantity: campaign.free_quantity || '0',
      item_free: campaign.item_free || null
    });
    setShowEditCampaign(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      offer_name: '',
      offer_type: 'flat',
      offer_scope: 'product',
      offer: '',
      min_cart_value: '0',
      product_id: '',
      product_name: '',
      offer_start: '',
      offer_end: '',
      status: 1,
      buy_quantity: '1',
      free_quantity: '0',
      item_free: null
    });
  };

  // Get offer type color
  const getOfferTypeColor = (type) => {
    const colors = {
      'percent': '#f59e0b',
      'flat': '#f97316',
      'bogo': '#fb923c',
      'combo': '#fdba74'
    };
    return colors[type] || '#6b7280';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Handle Add Offer Success/Cancel
  const handleAddOfferSuccess = () => { setShowAddOffer(false); fetchCampaigns(); };
  const handleAddOfferCancel = () => { setShowAddOffer(false); };

  // 🎨 ORANGE THEME CONFIGURATION
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    hoverBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
    success: '#16a34a',
    danger: '#dc2626',
    shadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(249, 115, 22, 0.15)',
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1400px',
      margin: '0 auto',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      backgroundColor: theme.bg,
      minHeight: '100vh',
      transition: 'all 0.3s ease',
    },
    header: {
      marginBottom: '32px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '16px',
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    headerTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    headerSubtitle: {
      fontSize: '14px',
      color: theme.textSecondary,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
    },
    statBadge: {
      background: theme.hoverBg,
      color: theme.primaryDark,
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 700,
      border: `1px solid ${theme.border}`,
    },
    addButton: {
      padding: '10px 24px',
      borderRadius: '10px',
      border: 'none',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)',
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '16px',
      marginBottom: '32px',
    },
    statCard: {
      background: theme.cardBg,
      padding: '16px 20px',
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      transition: 'all 0.3s ease',
      boxShadow: theme.shadow,
    },
    statNumber: {
      fontSize: '24px',
      fontWeight: 800,
      color: theme.text,
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    statLabel: {
      fontSize: '13px',
      color: theme.textSecondary,
      marginTop: '4px',
    },
    tabsContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: `2px solid ${theme.border}`,
      paddingBottom: '12px',
      flexWrap: 'wrap',
    },
    tab: {
      padding: '8px 20px',
      borderRadius: '8px',
      border: 'none',
      background: 'transparent',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      color: theme.textSecondary,
      transition: 'all 0.2s ease',
    },
    activeTab: {
      background: theme.primaryGradient,
      color: '#ffffff',
    },
    campaignsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
      gap: '20px',
    },
    campaignCard: {
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '20px',
      border: `1px solid ${theme.border}`,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
    },
    discountBadge: (color) => ({
      display: 'inline-block',
      padding: '6px 14px',
      borderRadius: '20px',
      fontSize: '18px',
      fontWeight: 700,
      color: '#ffffff',
      background: color,
      marginBottom: '12px',
      boxShadow: '0 4px 8px rgba(249, 115, 22, 0.2)',
    }),
    campaignTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '6px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px',
    },
    campaignInfo: {
      fontSize: '13px',
      color: theme.textSecondary,
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    campaignProduct: {
      fontSize: '14px',
      fontWeight: 600,
      color: theme.text,
      marginBottom: '4px',
    },
    campaignMeta: {
      fontSize: '12px',
      color: theme.textSecondary,
      marginBottom: '12px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px',
      flexWrap: 'wrap',
    },
    editButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      borderRadius: '8px',
      border: `1px solid ${theme.border}`,
      background: 'transparent',
      color: theme.text,
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    statusBadge: (isActive) => ({
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
      color: isActive ? '#16a34a' : '#dc2626',
    }),
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      gap: '16px',
    },
    loadingSpinner: {
      animation: 'spin 1s linear infinite',
      fontSize: '40px',
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: theme.textSecondary,
      fontSize: '16px',
      gridColumn: '1 / -1',
    },
    overlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    modal: {
      background: theme.cardBg,
      borderRadius: '20px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      padding: '32px',
      position: 'relative',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: `2px solid ${theme.border}`,
    },
    modalTitle: {
      fontSize: '22px',
      fontWeight: 800,
      color: theme.text,
      margin: 0,
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: theme.textSecondary,
      padding: '4px 8px',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
    },
    formGroup: { marginBottom: '20px' },
    formLabel: {
      display: 'block',
      fontSize: '13px',
      fontWeight: 600,
      color: theme.textSecondary,
      marginBottom: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    formInput: {
      width: '100%',
      padding: '10px 14px',
      borderRadius: '10px',
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.inputBg,
      color: theme.inputText,
      fontSize: '14px',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
    },
    offerTypeGroup: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    },
    offerTypeButton: (isActive) => ({
      padding: '8px 16px',
      borderRadius: '8px',
      border: `2px solid ${isActive ? theme.primary : theme.border}`,
      background: isActive ? 'rgba(249, 115, 22, 0.15)' : 'transparent',
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      color: isActive ? theme.primary : theme.textSecondary,
    }),
    submitButton: {
      width: '100%',
      padding: '12px',
      borderRadius: '10px',
      border: 'none',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginTop: '8px',
      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
    },
    toggleButton: (isActive) => ({
      padding: '6px 12px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: isActive ? '#dcfce7' : '#fee2e2',
      color: isActive ? '#16a34a' : '#dc2626',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    }),
    deleteButton: {
      padding: '6px 12px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: '#fee2e2',
      color: '#dc2626',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingContainer}>
          <FaSpinner style={styles.loadingSpinner} />
          <div style={{ color: theme.textSecondary }}>Loading campaigns...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <FaExclamationTriangle style={{ fontSize: '48px', marginBottom: '16px', color: theme.danger }} />
          <h3 style={{ color: theme.text }}>Failed to load campaigns</h3>
          <p>{error}</p>
          <button onClick={fetchCampaigns} style={{ marginTop: '16px', padding: '8px 24px', background: theme.primaryGradient, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)' }}>Retry</button>
        </div>
      </div>
    );
  }

  if (showAddOffer) {
    return <AddOffer restaurantId={restaurantId} onSuccess={handleAddOfferSuccess} onCancel={handleAddOfferCancel} isDark={isDark} />;
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .campaign-card:hover { box-shadow: 0 8px 24px rgba(249, 115, 22, 0.1); transform: translateY(-2px); }
        .modal-content { animation: slideUp 0.3s ease; }
      `}</style>

      {/* 👇 UPDATED HEADER: Removed "Campaigns" title, enhanced subtitle */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerTitle}>
            <FaTag style={{ color: theme.primary }} /> Promotional Offers
          </div>
          <div style={styles.headerSubtitle}>
            <span style={styles.statBadge}>{campaigns.length} Total Campaigns</span>
            {restaurantId && (
              <span style={{ fontSize: '13px', color: theme.textSecondary }}>
                Restaurant ID: <strong style={{ color: theme.text }}>{restaurantId}</strong>
              </span>
            )}
          </div>
        </div>
        <button style={styles.addButton} onClick={() => setShowAddOffer(true)}>
          <FaPlus /> Create Campaign
        </button>
      </div>

      {/* Stats */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{stats.total}</div>
          <div style={styles.statLabel}>Total Campaigns</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statNumber, color: '#16a34a' }}>
            {campaigns.filter(c => parseInt(c.status) === 1).length}
          </div>
          <div style={styles.statLabel}>Active</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statNumber, color: '#6b7280' }}>
            {campaigns.filter(c => parseInt(c.status) === 0).length}
          </div>
          <div style={styles.statLabel}>Inactive</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {['active', 'inactive'].map((tab) => (
          <button
            key={tab}
            style={{ ...styles.tab, ...(activeTab === tab ? styles.activeTab : {}) }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'active' ? 'Active' : 'Inactive'}
          </button>
        ))}
      </div>

      {/* Campaigns Grid */}
      <div style={styles.campaignsGrid}>
        {filteredCampaigns.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '48px', marginBottom: '16px', color: theme.textSecondary }}>📋</div>
            No {activeTab} campaigns found
          </div>
        ) : (
          filteredCampaigns.map((campaign) => {
            const offerColor = getOfferTypeColor(campaign.offer_type);
            const isActive = parseInt(campaign.status) === 1;

            return (
              <div
                key={campaign.id}
                className="campaign-card"
                style={{
                  ...styles.campaignCard,
                  ...(hoveredCard === campaign.id ? { boxShadow: '0 8px 24px rgba(249, 115, 22, 0.1)', transform: 'translateY(-2px)' } : {}),
                }}
                onMouseEnter={() => setHoveredCard(campaign.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={styles.discountBadge(offerColor)}>
                  {campaign.discountDisplay}
                </div>

                <div style={styles.campaignTitle}>
                  {campaign.offer_name}
                  <span style={styles.statusBadge(isActive)}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div style={styles.campaignProduct}>
                  <FaUtensils style={{ marginRight: '6px', fontSize: '12px', color: theme.textSecondary }} />
                  {campaign.product_name || 'All Products'}
                </div>

                <div style={styles.campaignMeta}>
                  <span><FaTag style={{ marginRight: '4px' }} />{campaign.offerScopeLabel}</span>
                  {campaign.offer_scope === 'cart' && campaign.min_cart_value > 0 && (
                    <span>Min: ₹{campaign.min_cart_value}</span>
                  )}
                  {campaign.offer_type === 'bogo' && (
                    <span>Buy {campaign.buy_quantity} Get {campaign.free_quantity} Free</span>
                  )}
                </div>

                <div style={styles.campaignInfo}>
                  <FaClock size={12} style={{ opacity: 0.6 }} />
                  {campaign.offer_end ? `Ends: ${formatDate(campaign.offer_end)}` : 'No expiry'}
                </div>

                <div style={styles.campaignInfo}>
                  <FaInfoCircle size={12} style={{ opacity: 0.6 }} />
                  ID: {campaign.id} · Created: {formatDate(campaign.created_at)}
                </div>

                <div style={styles.actionButtons}>
                  <button
                    style={styles.toggleButton(isActive)}
                    onClick={() => toggleStatus(campaign.id)}
                    disabled={updatingId === campaign.id}
                  >
                    {updatingId === campaign.id ? (
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    ) : isActive ? (
                      <FaToggleOn />
                    ) : (
                      <FaToggleOff />
                    )}
                    {isActive ? 'Active' : 'Inactive'}
                  </button>

                  <button style={styles.editButton} onClick={() => handleEdit(campaign)}>
                    <FaEdit /> Edit
                  </button>

                  <button
                    style={styles.deleteButton}
                    onClick={() => deleteCampaign(campaign.id)}
                    disabled={updatingId === campaign.id}
                  >
                    {updatingId === campaign.id ? (
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <FaTrash />
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Edit Campaign Modal */}
      {showEditCampaign && selectedCampaign && (
        <div style={styles.overlay} onClick={() => setShowEditCampaign(false)}>
          <div className="modal-content" style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit Campaign</h2>
              <button style={styles.closeButton} onClick={() => setShowEditCampaign(false)}>✕</button>
            </div>

            <form onSubmit={handleUpdate}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Campaign Name</label>
                <input type="text" name="offer_name" placeholder="e.g. ₹20 OFF" style={styles.formInput} value={formData.offer_name} onChange={handleInputChange} required />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Offer Applies To</label>
                <div style={styles.offerTypeGroup}>
                  {['product', 'cart'].map((scope) => (
                    <button key={scope} type="button" style={styles.offerTypeButton(formData.offer_scope === scope)} onClick={() => setFormData({...formData, offer_scope: scope})}>
                      {scope === 'product' ? <FaUtensils /> : <FaShoppingCart />} {scope.charAt(0).toUpperCase() + scope.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Offer Type</label>
                <div style={styles.offerTypeGroup}>
                  {['flat', 'percent', 'bogo', 'combo'].map((type) => (
                    <button key={type} type="button" style={styles.offerTypeButton(formData.offer_type === type)} onClick={() => setFormData({...formData, offer_type: type})}>
                      {type === 'percent' && <FaPercent />}
                      {type === 'flat' && <FaRupeeSign />}
                      {type === 'bogo' && 'BOGO'}
                      {type === 'combo' && 'COMBO'}
                      {' '}{type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  {formData.offer_type === 'percent' ? 'Discount %' : formData.offer_type === 'flat' ? 'Discount Amount (₹)' : formData.offer_type === 'bogo' ? 'Buy Quantity' : 'Combo Details'}
                </label>
                <input type="number" name="offer" placeholder={formData.offer_type === 'percent' ? 'e.g. 20' : formData.offer_type === 'flat' ? 'e.g. 100' : formData.offer_type === 'bogo' ? 'e.g. 1' : 'e.g. 0'} style={styles.formInput} value={formData.offer} onChange={handleInputChange} required />
              </div>

              {formData.offer_type === 'bogo' && (
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Free Quantity</label>
                    <input type="number" name="free_quantity" placeholder="e.g. 1" style={styles.formInput} value={formData.free_quantity} onChange={handleInputChange} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Item Free (ID)</label>
                    <input type="text" name="item_free" placeholder="Product ID" style={styles.formInput} value={formData.item_free || ''} onChange={handleInputChange} />
                  </div>
                </div>
              )}

              {formData.offer_scope === 'cart' && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Minimum Cart Value (₹)</label>
                  <input type="number" name="min_cart_value" placeholder="e.g. 500" style={styles.formInput} value={formData.min_cart_value} onChange={handleInputChange} />
                </div>
              )}

              {formData.offer_scope === 'product' && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Product Name</label>
                  <input type="text" name="product_name" placeholder="Product name" style={styles.formInput} value={formData.product_name} onChange={handleInputChange} />
                </div>
              )}

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Start Date</label>
                  <input type="date" name="offer_start" style={styles.formInput} value={formData.offer_start} onChange={handleInputChange} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>End Date</label>
                  <input type="date" name="offer_end" style={styles.formInput} value={formData.offer_end} onChange={handleInputChange} />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Status</label>
                <div style={styles.offerTypeGroup}>
                  <button type="button" style={styles.offerTypeButton(parseInt(formData.status) === 1)} onClick={() => setFormData({...formData, status: 1})}>
                    <FaToggleOn /> Active
                  </button>
                  <button type="button" style={styles.offerTypeButton(parseInt(formData.status) === 0)} onClick={() => setFormData({...formData, status: 0})}>
                    <FaToggleOff /> Inactive
                  </button>
                </div>
              </div>

              <button type="submit" style={styles.submitButton} disabled={updatingId === 'edit'}>
                {updatingId === 'edit' ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : 'Update Campaign'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;